import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Progress } from '@sorare/core/src/atoms/loader/Progress';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import {
  BrandXXL,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { AnyRewardConfig } from '@sorare/core/src/components/rewards/AnyRewardConfig';
import { isType } from '@sorare/core/src/lib/gql';

import type { CardCollectionDetailsDialog_taskInterface } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3 })`
  padding: var(--double-unit);
`;
const Thresholds = styled(Vertical).attrs({ gap: 2 })`
  overflow: hidden;
  padding: var(--triple-unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--double-unit);
`;
const Threshold = styled(Vertical).attrs({ gap: 1.5 })`
  &.disabled {
    opacity: 0.5;
  }
`;
const CollectionScoreWrapper = styled.div`
  position: relative;
  isolation: isolate;
`;
const CollectionScore = styled(BrandXXL)`
  text-shadow: 0 0 8px var(--c-nd-500);
`;
const AbsoluteScarcityIcon = styled(ScarcityIcon).attrs({ size: 4 })`
  position: absolute;
  top: 50%;
  right: calc(-1 * var(--double-unit));
  z-index: -1;
  transform: translateY(-50%) rotate(15deg);
`;

type Props = {
  open?: boolean;
  onClose?: () => void;
  task: CardCollectionDetailsDialog_taskInterface;
};
export const CardCollectionDetailsDialog = ({ open, onClose, task }: Props) => {
  if (!isType(task, 'CardsCountTask')) {
    return null;
  }

  const { title, iconUrl, thresholds, liveScore, countTaskRarity } = task;

  const currentThresholdIndex = thresholds.findIndex(
    ({ current }) => !!current
  );
  const previousThreshold = thresholds[currentThresholdIndex - 1];
  const currentThreshold = thresholds[currentThresholdIndex];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <Horizontal center>
          <LabelL bold>{title}</LabelL>
          {!!iconUrl && <img src={iconUrl} width={24} height={24} alt="" />}
        </Horizontal>
      }
      maxWidth="sm"
    >
      <Root>
        <Vertical gap={0.5} center>
          <CollectionScoreWrapper>
            <CollectionScore>{liveScore}</CollectionScore>
            {!!countTaskRarity && (
              <AbsoluteScarcityIcon rarity={countTaskRarity} />
            )}
          </CollectionScoreWrapper>
          <LabelM>
            <FormattedMessage defaultMessage="Cards collected" />
          </LabelM>
        </Vertical>
        <Thresholds>
          <Vertical>
            <SBHorizontal>
              <LabelS bold color="var(--c-nd-600)">
                {previousThreshold?.count || '-'}
              </LabelS>
              <LabelS bold>{currentThreshold?.count}</LabelS>
            </SBHorizontal>
            <Progress
              value={liveScore}
              max={currentThreshold?.count}
              min={previousThreshold?.count || 0}
              color="linear-gradient(to right, var(--c-yellow-500), var(--c-yellow-300))"
              bgColor="var(--c-nd-200)"
            />
          </Vertical>
          <Scrollable itemToDisplay={5} indexToScroll={currentThresholdIndex}>
            {thresholds.map(({ count, current, rewardConfigs }, i) => {
              const thresholdValidated = i < currentThresholdIndex;
              const [rewardConfig] = rewardConfigs;
              return (
                <Threshold
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  center
                  className={classNames({ disabled: !!thresholdValidated })}
                >
                  <AnyRewardConfig anyRewardConfig={rewardConfig} />
                  <Horizontal gap={0.5}>
                    {thresholdValidated ? (
                      <FontAwesomeIcon
                        icon={iconCheck}
                        color="var(--c-onBase-success)"
                      />
                    ) : (
                      !!countTaskRarity && (
                        <ScarcityIcon rarity={countTaskRarity} />
                      )
                    )}
                    <LabelS bold={current}>{count}</LabelS>
                  </Horizontal>
                </Threshold>
              );
            })}
          </Scrollable>
        </Thresholds>
      </Root>
    </Dialog>
  );
};

CardCollectionDetailsDialog.fragments = {
  taskInterface: gql`
    fragment CardCollectionDetailsDialog_taskInterface on TaskInterface {
      id
      target
      title
      iconUrl
      ... on CardsCountTask {
        countTaskRarity: rarity
        id
        liveScore
        thresholds {
          current
          count
          rewardConfigs {
            id
            ...AnyRewardConfig_anyRewardConfigInterface
          }
        }
      }
    }
    ${AnyRewardConfig.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<CardCollectionDetailsDialog_taskInterface>,
};
